import React, { useState, useEffect, useRef, ReactNode, forwardRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { theme } from "@upsolve/ui";
import { ERROR_MESSAGE, IChatMessage, TOO_MANY_MESSAGES_MESSAGE, useChat } from "./useChat";
import CloseButton from "../../../static/images/close-button.svg";
import SendIcon from "./images/send-icon.svg";
import Sparkles from "./images/sparkles-icon-dark.svg";
import PageIcon from "./images/page-icon.svg";
import MessageIcon from "./images/chat-icon.svg";
import { QuestionAnswerFormat } from "./QuestionAnswerFormat";
import { MessageText } from "./MessageText";
import { FeedbackButtons } from "./FeedbackButtons";
import trackComponentImpression from "../analytics/trackComponentImpression";
import trackComponentAction from "../analytics/trackComponentAction";

const StyledChatIntroMessage = styled.div`
  b {
    margin-bottom: 12px;
  }
  hr {
    border: none;
    border-top: 1px solid ${theme.colors.brand["600"]};
    margin: 16px 0;
  }
  ul {
    list-style-type: none;
    padding-left: 0;
  }
  ul li {
    display: flex;
    align-items: flex-start;
  }
  ul li::before {
    content: "✔";
    font-weight: bold;
    margin-right: 8px;
    flex-shrink: 0;
  }
  .check {
    padding-right: 8px;
  }
  p {
    margin-bottom: 8px;
    margin-top: 8px;
  }
`;

const OpeningMessage: React.FC = () => (
  <SystemMessage>
    <div className="title">
      <Sparkles />
      <span>Assistant</span>
    </div>
    <StyledChatIntroMessage>
      <b>Meet Upsolve's AI Assistant</b>
      <br />
      Debt is complicated- we're here to help! Ask any question about your current financial situation or your debt
      relief options. I will answer your questions based on Upsolve's expert-written articles. If upsolve doesn't have
      an article pertaining to your question, I will answer generally. I can only provide general information, not legal
      advice.
      <hr />
      Our AI Assistant is still learning, so please review all responses. Your questions and the AI answers are
      anonymously recorded. Learn more about how we build&nbsp;
      <a href="https://upsolve.org/learn/upsolve-commitments" target="_blank">
        reliable and safe
      </a>
      &nbsp;AI.
    </StyledChatIntroMessage>
  </SystemMessage>
);

const slideUp = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const ChatWindow = styled.div<{ isOpen: boolean; isMidClosingAnimation: boolean }>`
  position: fixed;
  z-index: 5000;
  top: var(--nav-header-height, 54px);
  right: 0;
  width: 400px;
  height: calc(100vh - var(--nav-header-height, 54px));
  background-color: ${theme.colors.white[900]};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: ${(props) => (props.isOpen || props.isMidClosingAnimation ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  animation: ${(props) =>
    props.isOpen
      ? css`
          ${slideUp} 0.3s ease-out forwards
        `
      : props.isMidClosingAnimation
      ? css`
          ${slideDown} 0.2s ease-in forwards
        `
      : "none"};

  @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
    top: 0;
    bottom: 82px;
    left: 0;
    right: 0;
    width: 100%;
    height: auto;
    border-radius: 0;
  }
`;

const ChatHeader = styled.div`
  color: ${theme.colors.brand[500]};
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .chat-title {
    font-size: 12px;
    display: flex;
    align-items: center;
  }
  svg {
    width: 32px;
  }
  button {
    all: unset;
    cursor: pointer;
  }
`;

const ChatMessages = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
`;

const UserMessage = styled.div`
  margin-bottom: 14px;
  padding: 16px 24px;
  border-radius: 18px;
  font-size: 16px;
  line-height: 1.4;
  text-align: left;
  position: relative;
  width: fit-content;
  max-width: 80%;
  align-self: flex-end;
  min-width: 0;
  background-color: #f5f5f8;
  color: black;
`;

const SystemMessage = styled.div`
  margin-bottom: 14px;
  padding: 16px 24px;
  border-radius: 18px;
  font-size: 16px;
  line-height: 1.4;
  text-align: left;
  position: relative;
  width: 100%;
  min-width: 0;

  background-color: #eceffd;
  color: black;

  .title {
    color: ${theme.colors.brand[500]};
    font-size: 12px;
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    span {
      padding-left: 5px;
    }
  }
`;

interface Article {
  title: string;
  url: string;
}

const StyledMessageFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

interface MessageFooterProps {
  messageId: number;
  wasHelpful?: boolean | null;
  onFeedback?: (messageId: number, wasHelpful: boolean | null) => void;
  article?: Article;
}

export const MessageFooter: React.FC<MessageFooterProps> = ({ messageId, wasHelpful, onFeedback, article }) => (
  <StyledMessageFooter>
    {!!messageId && !!onFeedback ? (
      <FeedbackButtons messageId={messageId} wasHelpful={wasHelpful} onFeedback={onFeedback} />
    ) : (
      <div></div>
    )}
    {article && <SourceButton article={article} />}
  </StyledMessageFooter>
);

const QAFormatIntroMessage =
  "I noticed you're asking a more complicated question. To answer effectively, I'll address each part separately.";

const Message = forwardRef<
  HTMLDivElement,
  { isUser: boolean; chat: IChatMessage; onFeedback: (messageId: number, wasHelpful: boolean | null) => void }
>((props, ref): JSX.Element => {
  const MessageContent = () => {
    const articles: Article[] = (() => {
      try {
        return props.chat.articleLink ? JSON.parse(props.chat.articleLink) : [];
      } catch (e) {
        console.error("Failed to parse article link:", e);
        return [];
      }
    })();

    if (props.chat.text.includes("Question: ") && props.chat.text.includes("Answer: ")) {
      return (
        <>
          <QuestionAnswerFormat text={props.chat.text} articles={articles} />
        </>
      );
    }
    const shouldHideFooter =
      props.chat.text.includes(ERROR_MESSAGE) ||
      props.chat.text.includes(TOO_MANY_MESSAGES_MESSAGE) ||
      props.chat.text.includes(QAFormatIntroMessage);

    return (
      <StyledMessageContent>
        <MessageText text={props.chat.text} />
        {props.chat.sender === "ai" && !shouldHideFooter && (
          <MessageFooter
            messageId={props.chat.id}
            wasHelpful={props.chat.wasHelpful ?? null}
            onFeedback={props.onFeedback}
            article={articles[0]}
          />
        )}
      </StyledMessageContent>
    );
  };

  if (props.isUser) {
    return (
      <UserMessage ref={ref}>
        <MessageContent />
      </UserMessage>
    );
  }
  return (
    <SystemMessage ref={ref}>
      <div className="title">
        <Sparkles />
        <span>Assistant</span>
      </div>
      <MessageContent />
    </SystemMessage>
  );
});

const SendMessageWrapper = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 15px;
  background-color: ${theme.colors.white[900]};
  z-index: 6000;
`;

const SendMessageBox = styled.div`
  box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.15);
  width: 100%;
  display: flex;
  border-radius: 12px;
`;

const SendMessageInputWrapper = styled.div`
  flex-grow: 1;
  background-color: #eef1fe;
  border: 1px solid #3c5dff;
  border-radius: 12px 0 0 12px;
  height: 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SendMessageIconWrapper = styled.span`
  padding-left: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SendMessageInputField = styled.input`
  background-color: #eef1fe;
  border: 0;
  font-size: 16px;
  &:focus {
    outline: none;
  }
  ::placeholder {
    color: #3c5dff;
    opacity: 50%;
  }
  flex-grow: 1;
  padding: 8px 8px;
  overflow: hidden;
`;

const SendButton = styled.button`
  height: 50px;
  background-color: ${theme.colors.brand[500]};
  color: ${theme.colors.white[900]};
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  border-radius: 0 12px 12px 0;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
`;

const LoadingDot = styled.span`
  width: 8px;
  height: 8px;
  background-color: ${theme.colors.brand[500]};
  border-radius: 50%;
  display: inline-block;
  margin: 0 4px;
  animation: ${css`
    ${pulse} 1.4s infinite ease-in-out both
  `};

  &:nth-child(1) {
    animation-delay: -0.32s;
  }
  &:nth-child(2) {
    animation-delay: -0.16s;
  }
`;

const StyledSource = styled.button`
  background: none;
  border: none;
  margin-top: 6px;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.brand[500]};
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  min-height: 22px;
  transition: color 0.2s ease;
  &:hover {
    color: ${theme.colors.brand[600]};
  }
  span {
    padding-left: 6px;
  }
`;

export const NoSourceDisclaimer = styled.span`
  color: #888;
  font-size: 14px;
`;

const ChatContainer = styled.div<{ isOpen?: boolean }>`
  position: relative;
  height: 100%;
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  z-index: ${(props) => (props.isOpen ? "5000" : "-1")};

  @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${(props) => (props.isOpen ? "5000" : "-1")};
  }
`;

const StyledMessageContent = styled.div`
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const SourceButton: React.FC<{
  article: { title: string; url: string };
}> = ({ article }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    trackComponentAction({ componentName: "topOfFunnelAi", actionId: "sourceClicked", actionMethod: "click" });

    if (article.url) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.opener = null;
        newWindow.location.href = article.url;
      }
    }
  };

  return (
    <StyledSource onClick={handleClick} aria-label={`Open article: ${article.title}`}>
      <PageIcon />
      <span>VERIFIED</span>
    </StyledSource>
  );
};

export const TopOfFunnelAIChat = () => {
  const chat = useChat();
  const [isMidClosingAnimation, setIsMidClosingAnimation] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [impressionTracked, setImpressionTracked] = useState(false);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle header height changes like when HelloBar is closed
  useEffect(() => {
    const updateHeaderHeight = () => {
      const navHeader = document.querySelector("header");
      if (navHeader) {
        document.documentElement.style.setProperty("--nav-header-height", `${navHeader.offsetHeight}px`);
      }
    };

    // Initial calculation
    updateHeaderHeight();

    // Set up observer for changes to the header
    const headerObserver = new MutationObserver(updateHeaderHeight);
    const header = document.querySelector("header");
    if (header) {
      headerObserver.observe(header, { attributes: true, subtree: true, childList: true });
    }

    return () => {
      headerObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!chat.isNearBottom()) return;
    if (!lastMessageRef.current && !chat.isLoading) return;

    requestAnimationFrame(() => {
      lastMessageRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [chat.history, chat.isLoading]);

  useEffect(() => {
    if (chat.isOpen) {
      if (window.innerWidth <= 720) {
        // On mobile, blur and refocus the input to ensure the keyboard pushes the UI up
        inputRef.current?.blur();
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } else {
        inputRef.current?.focus();
      }
      if (!impressionTracked) {
        trackComponentImpression({ componentName: "topOfFunnelAi" });
        setImpressionTracked(true);
      }
    }
  }, [chat.isOpen, impressionTracked]);

  const handleClose = () => {
    setIsMidClosingAnimation(true);
    chat.setIsOpen(false);
    setTimeout(() => {
      setIsMidClosingAnimation(false);
    }, 300);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || chat.isLoading) return;
    setUserInput("");
    await chat.askQuestion(userInput, false);
    trackComponentAction({ componentName: "topOfFunnelAi", actionId: "messageSent", actionMethod: "send" });
  };

  const handleFeedback = (messageId: number, isHelpful: boolean | null) => {
    chat.updateMessageFeedback(messageId, isHelpful);
  };

  return (
    <ChatContainer isOpen={chat.isOpen || isMidClosingAnimation}>
      <ChatWindow isOpen={chat.isOpen} isMidClosingAnimation={isMidClosingAnimation}>
        <ChatHeader>
          <div className="chat-title">
            <Sparkles />
            <span>Assistant</span>
          </div>
          <button onClick={handleClose}>
            <CloseButton />
          </button>
        </ChatHeader>
        <ChatMessages ref={chat.chatContainerRef}>
          <OpeningMessage />
          {chat.history.map((message, index) => (
            <Message
              key={message.id}
              isUser={message.sender === "user"}
              ref={index === chat.history.length - 1 ? lastMessageRef : null}
              chat={message}
              onFeedback={handleFeedback}
            />
          ))}
          {chat.isLoading && (
            <LoadingWrapper>
              <LoadingDot />
              <LoadingDot />
              <LoadingDot />
            </LoadingWrapper>
          )}
        </ChatMessages>
        <SendMessageWrapper>
          <SendMessageBox>
            <SendMessageInputWrapper>
              <SendMessageIconWrapper>
                <MessageIcon />
              </SendMessageIconWrapper>
              <SendMessageInputField
                ref={inputRef}
                value={userInput}
                onChange={(e) => {
                  setUserInput(e.target.value);
                }}
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder="Message..."
              />
            </SendMessageInputWrapper>
            <SendButton onClick={handleSendMessage} disabled={chat.isLoading}>
              <SendIcon />
            </SendButton>
          </SendMessageBox>
        </SendMessageWrapper>
      </ChatWindow>
    </ChatContainer>
  );
};
